import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {Attachment, BodyAndAttachments, Mail, MailFolderEnum, WebmailApi} from "components/src/api/portal/generated";
import {AccessibleSpinner} from "components/src/common/AccessibleSpinner";
import {Attachments} from "components/src/common/Attachments";
import {ConfirmDialog} from "components/src/common/ConfirmDialog";
import {Download} from "components/src/common/Download";
import {DialogCloseCause} from "components/src/common/Enums";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {DateUtils} from "components/src/utils/DateUtils";
import {StringUtils} from "components/src/utils/StringUtils";
import {Utils} from "components/src/utils/Utils";
import DOMPurify from 'dompurify';
import {
    useEnterprisePortalPropertiesContext
} from "enterprise-components/src/hook/UseEnterprisePortalPropertiesContext";
import {CloudDownload as DownloadIcon, Reply as ReplyIcon, Timer, Trash as DeleteIcon} from "iconoir-react";
import {useEffect, useState} from "react";
import Button from "react-bootstrap/Button";
import FloatingLabel from "react-bootstrap/esm/FloatingLabel";
import Form from "react-bootstrap/esm/Form";
import Modal from "react-bootstrap/Modal";
import {useNavigate} from "react-router-dom";
import {ReplyPopup} from "./ReplyPopup";

/*
 * REST api for accessing the back-end
 */
const webmailApi = new WebmailApi(ApiConfig.config());

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface ViewMailProps {
    mail : Mail
    onClose : OnClose
}

export const ViewMailPopup = ({mail, onClose} : ViewMailProps) => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated or to some other page
     * if the dialog is closed
     */
    const navigate = useNavigate();
    /**
     * The HTML bodies of the message
     */
    const [bodyAndAttachments, setBodyAndAttachments] = useState<BodyAndAttachments | undefined>(undefined);
    /**
     * If true, the "confirm delete" modal dialog will be shown
     */
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * If true, a Download component will be loaded which will download the binary property
     */
    const [download, setDownload] = useState(false);
    /**
     * The blob containing the binary file to download
     */
    const [blob, setBlob] = useState<Blob>();
    /**
     * The filename for the downloaded file.
     */
    const [filename, setFilename] = useState<string>();
    /**
     * If true, the reply popup will be shown
     */
    const [showReplyPopup, setShowReplyPopup] = useState<boolean>(false);
    /**
     * true if a reply to the sender is allowed
     */
    const [isRecipientAllowed, setIsRecipientAllowed] = useState<boolean>(false);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {spinnerStateCounter, setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();
    const {properties} = useEnterprisePortalPropertiesContext();
    const isReplyAllowed = properties?.webmailIsReplyAllowed ?? false;

    useEffect(() => {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        webmailApi.webmailPortalControllerGetBodyAndAttachments(mail.folder, mail.uid)
            .then((response) => {
                setBodyAndAttachments(response.data);
            })
            .catch((err: Error | AxiosError) => {
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
        })
    }, [mail.folder,
        mail.uid,
        navigate,
        setSpinnerStateCounter,
        t]);


    useEffect(() => {
        if (mail.from === undefined) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        webmailApi.webmailPortalControllerIsRecipientAllowed(mail.from)
            .then(response => {
                setIsRecipientAllowed(response.data);
            })
            .catch((err: Error | AxiosError) => {
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [navigate,
        setSpinnerStateCounter,
        t,
        mail.from]);

    const deleteMessage = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        webmailApi.webmailPortalControllerDeleteMessages(mail.folder, [mail.uid])
            .then(() => {
                closePage(DialogCloseCause.Succes);
                setShowDeleteConfirm(false);
            })
            .catch((err: Error | AxiosError) => {
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
        })
    }

    const downloadAttachment = (attachment: Attachment) =>
    {
        setFilename(attachment.filename);

        webmailApi.webmailPortalControllerGetAttachment(mail.folder, mail.uid, attachment.index,
            {responseType: 'blob'}).then((response) => {
            setBlob(new Blob([response.data], {type: "application/octet-stream"}));

            // enable the Download component
            setDownload(true);
        })
        .catch((err: Error | AxiosError) => {
            Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
    }

    const downloadMessage = () =>
    {
        setFilename(mail.subject + ".eml");

        // as a blob, because a message is bytes and not text: without it the response is decoded as UTF-8
        // and every byte which is not valid UTF-8 is saved as a replacement character
        webmailApi.webmailPortalControllerGetMimeMessage(mail.folder, mail.uid,
            {responseType: 'blob'}).then((response) =>
        {
            setBlob(new Blob([response.data], {type: "message/rfc822"}));

            // enable the Download component
            setDownload(true);
        })
        .catch((err: Error | AxiosError) => {
            Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
    }

    /**
     * Is called when the download started. This should reset download to make sure the Download component is no
     * longer added.
     */
    const onDownload = () => {
        // disable the Download component
        setDownload(false);
    }

    const sanitizeHTML = (html: string) => {
        return {__html: DOMPurify.sanitize(html)};
    }

    const reply = () => {
        setShowReplyPopup(true);
    }

    const hideReplyPopup = () => {
        setShowReplyPopup(false);
    }

    const closePage = (dialogCloseCause : DialogCloseCause) => {
        onClose(dialogCloseCause);
    }

    return (
        <>
            <ConfirmDialog title={t("common.confirm")}
                           bodyText={t("portal.messages.confirm-delete-message")}
                           confirmButtonTitle={t("common.delete")}
                           onClickConfirmEvent={() => deleteMessage()}
                           showConfirm={showDeleteConfirm}
                           setShowConfirm={setShowDeleteConfirm}
            />

            {showReplyPopup && <ReplyPopup sourceMail={mail} sourceBody={bodyAndAttachments?.text}
                                           onClose={hideReplyPopup}/>}

            <div>
                <Modal show={true}
                       onHide={() => closePage(DialogCloseCause.Cancel)}
                       backdrop="static"
                       scrollable={true}
                       dialogClassName={"cm-modal-dialog"}
                       contentClassName={"cm-modal-content"}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {mail.subject}
                        </Modal.Title>

                        <div className="d-flex flex-fill justify-content-end align-items-center">
                            <AccessibleSpinner show={spinnerStateCounter > 0}/>
                            <span className="ms-2 text-muted"><Timer/> {DateUtils.fromTimestampToLocaleDateStringShort(mail.date)}</span>
                        </div>
                    </Modal.Header>
                    {/*
                     * overflow-hidden overrides Bootstrap's scrollable modal body (overflow-y: auto)
                     * so that only the <main> region scrolls, not the headers and attachments.
                     */}
                    <Modal.Body className="d-flex flex-column overflow-hidden">
                        {/* Fixed section — headers and attachments stay visible while mail body scrolls */}
                        <div className="flex-shrink-0">
                            <FloatingLabel label={t('portal.messages.from')} className="mb-2">
                                <Form.Control
                                    type="text"
                                    value={StringUtils.unquoteEmail(mail.from)}
                                    placeholder="From"
                                    readOnly={true}
                                />
                            </FloatingLabel>

                            <FloatingLabel label={t('portal.messages.to')} className="mb-2">
                                <Form.Control
                                    type="text"
                                    value={mail.to?.map(e => StringUtils.unquoteEmail(e)).join(", ")}
                                    placeholder="To"
                                    readOnly={true}
                                />
                            </FloatingLabel>

                            {mail.cc && mail.cc.length > 0 &&
                                <FloatingLabel label={t('portal.messages.cc')} className="mb-2">
                                    <Form.Control
                                        type="text"
                                        value={mail.cc?.map(e => StringUtils.unquoteEmail(e)).join(", ")}
                                        placeholder="Cc"
                                        readOnly={true}
                                    />
                                </FloatingLabel>
                            }

                            <Attachments
                                attachments={(bodyAndAttachments?.attachments ?? []).map(a => ({name: a.filename, size: a.size}))}
                                dropdownItemText={t('portal.messages.download-attachment')}
                                onDropdownItemClick={(index) => downloadAttachment(bodyAndAttachments!.attachments[index])}
                                icon={<DownloadIcon className="me-1"/>}
                            />
                        </div>

                        {/* Scrollable mail body */}
                        <main className="flex-grow-1 overflow-auto modal-main-body mt-2">
                            <RenderAlert message={errorMessage}
                                         setMessage={setErrorMessage}
                                         tooltipPlacement={"bottom"}/>

                            {bodyAndAttachments?.html.map((htmlBody, index) => (
                                <div key={index} dangerouslySetInnerHTML={sanitizeHTML(htmlBody)}/>
                            ))}
                        </main>
                    </Modal.Body>

                    <Modal.Footer className="justify-content-between">
                        <div className="d-flex flex-fill justify-content-start align-items-center">
                            <Button variant="danger"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => setShowDeleteConfirm(true)}>
                                <DeleteIcon className="me-1"/> {t("portal.messages.delete")}
                            </Button>

                            <Button variant="outline-secondary"
                                    className="d-none d-sm-inline"
                                    size="sm"
                                    onClick={() => downloadMessage()}>
                                <DownloadIcon className="me-1"/> {t("portal.messages.download-message")}
                            </Button>
                        </div>

                        <div className="d-flex flex-fill justify-content-end align-items-center">
                            <Button variant="outline-secondary"
                                    size="sm"
                                    onClick={() => reply()}
                                    disabled={
                                        !isReplyAllowed ||
                                        !isRecipientAllowed ||
                                        mail.folder != MailFolderEnum.Inbox ||
                                        StringUtils.isBlank(mail.from)}>
                                <ReplyIcon className="me-1"/> {t("portal.messages.reply")}
                            </Button>
                        </div>
                    </Modal.Footer>

                    {download && blob !== undefined && (
                        <Download blob={blob}
                                  filename={filename??"unknown"}
                                  onDownload={onDownload}/>
                    )}
                </Modal>
            </div>
        </>
    );
}
